import React, { useState, useEffect, useContext } from "react";

import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import ErrorBanner from "../components/ui/ErrorBanner";
import { IoMdArrowBack } from "react-icons/io";
import { IoClose } from "react-icons/io5";

import { useNavigate } from "react-router-dom";
import { useLoading } from "../context/LoadingContext";
import { fetchPromotionContent } from "../utils/api";
import FloatingMenu from "../components/FloatingMenu";
import config from "../config";
import LanguageContext from "../context/LanguageContext";

const Promotion = () => {
    const { t } = useTranslation();
    const { setLoading } = useLoading();
    const navigate = useNavigate();
    const { getLanguageId } = useContext(LanguageContext);

    const [errorMessage, setErrorMessage] = useState("");
    const [promotionContentLoading, setPromotionContentLoading] = useState(true);
    const [promotionContentList, setPromotionContentList] = useState([]);
    const [activeTab, setActiveTab] = useState(0);
    const [expandedId, setExpandedId] = useState(null);
    const selectedLanguageId = getLanguageId() || Number(localStorage.getItem("language_id")) || 1;

    const formatDate = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        return date.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    };

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            setPromotionContentLoading(true);
            setErrorMessage("");

            try {
                const response = await fetchPromotionContent({ language_id: selectedLanguageId });
                if (response.success && Array.isArray(response.data) && response.data.length > 0) {
                    setPromotionContentList(response.data);
                } else {
                    setPromotionContentList([]);
                }
            } catch (error) {
                setErrorMessage(t("pleaseTryLater"));
            } finally {
                setLoading(false);
                setPromotionContentLoading(false);
            }
        };

        loadData();
    }, [setLoading, t, selectedLanguageId]);

    useEffect(() => {
        if (errorMessage) {
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    }, [errorMessage]);

    return (
        <section className="page-shell">
            <div className="page-header">
                <div
                    className="cursor-pointer bg-secondary container-back-button text-color-blue rounded-full"
                    onClick={() => navigate("/")}
                >
                    <IoMdArrowBack className="size-sm" />
                    <span className="text-sm">
                        {t("backToLobby")}
                    </span>
                </div>
                <button className="cursor-pointer" onClick={() => navigate("/")}>
                    <IoClose className="size-lg text-color-blue" />
                </button>
            </div>

            <div className="page-content">
                <ErrorBanner message={errorMessage} />

                <div className="flex overflow-x-auto whitespace-nowrap gap-5 bg-option-blue px-4 lg:rounded-[2rem] lg:px-6">
                    {[
                        { key: "showAll", label: t("showAll") },
                        { key: "vip", label: t("vip") },
                    ].map((tab, idx) => {
                        const isActive = activeTab === idx;

                        return (
                            <button
                                key={tab.key}
                                onClick={() => setActiveTab(idx)}
                                className={`relative py-3 text-sm transition-all duration-200 ${
                                    isActive ? "text-white font-bold" : "text-color-white-opacity"
                                }`}
                            >
                                {tab.label}
                                {isActive && (
                                    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-1 bg-button-blue rounded-full" />
                                )}
                            </button>
                        );
                    })}
                </div>

                <div className="pt-4 lg:px-0 lg:pt-6 lg:pb-6">
                    {promotionContentLoading ? (
                        <div className="space-y-4 lg:grid lg:grid-cols-2 lg:gap-6 lg:space-y-0">
                            {[...Array(3)].map((_, index) => (
                                <div key={index} className="bg-option-blue rounded-lg p-5 lg:rounded-[2rem] lg:p-6">
                                <div className="relative overflow-hidden mb-3">
                                    <Skeleton 
                                        height={160} 
                                        borderRadius="0.5rem"
                                        baseColor="#d1d5db"
                                        highlightColor="#f3f4f6"
                                    />
                                </div>

                                <div className="mb-2">
                                    <Skeleton 
                                        width="60%" 
                                        height={20} 
                                        baseColor="#d1d5db"
                                        highlightColor="#f3f4f6"
                                    />
                                </div>

                                <div className="mb-4">
                                    <Skeleton 
                                        count={2} 
                                        height={14} 
                                        baseColor="#d1d5db"
                                        highlightColor="#f3f4f6"
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                    <Skeleton 
                                        height={36}
                                        baseColor="#d1d5db"
                                        highlightColor="#f3f4f6"
                                    />
                                    <Skeleton 
                                        height={36}
                                        baseColor="#d1d5db"
                                        highlightColor="#f3f4f6"
                                    />
                                </div>
                            </div>
                            ))}
                        </div>
                    ) : promotionContentList.length > 0 ? (
                        <div className="space-y-4 lg:grid lg:grid-cols-2 lg:gap-6 lg:space-y-0">
                            {promotionContentList.map((promo, index) => {
                            const getTranslation = (key) => promo.translations.find(t => t.key === key)?.content || "";

                            const title = getTranslation('title');
                            const description = getTranslation('short_description');
                            const imageUrl = getTranslation('image');
                            const howToApply = getTranslation('how_to_apply');
                            const details = getTranslation('details');
                            const tnc = getTranslation('tnc');

                            const isExpanded = expandedId === promo.id;

                            return (
                                <div 
                                    key={promo.id || index} 
                                    className="bg-option-blue rounded-lg p-5 lg:rounded-[2rem] lg:p-6"
                                >
                                    <div className="relative overflow-hidden mb-3">
                                        <img 
                                            src={`${config.APP_URL}${imageUrl}`}
                                            className="w-full h-auto object-cover"
                                            alt="promotion"
                                        />
                                    </div>
                                                
                                    {/* <div className="flex gap-2 mb-3">
                                        <span className="bg-dropdown-bg text-white text-xxs px-3 h-6 rounded-lg uppercase inline-flex items-center justify-center leading-none">
                                            {promo.category}
                                        </span>
                                        <span className="bg-dropdown-bg text-white text-xxs px-3 h-6 rounded-lg uppercase inline-flex items-center justify-center leading-none">
                                            {promo.type}
                                        </span>
                                    </div> */}

                                    <div className="text-white font-bold text-sm mb-1">
                                        <style>{`.html-content * { color: white !important; }`}</style>
                                        <div 
                                            className="whitespace-pre-line html-content"
                                            dangerouslySetInnerHTML={{ __html: title }}
                                        />
                                    </div>
                                    <div className="text-white text-sm mb-3">
                                        <style>{`.html-content * { color: white !important; }`}</style>
                                        <div 
                                            className="whitespace-pre-line html-content"
                                            dangerouslySetInnerHTML={{ __html: description }}
                                        />
                                    </div>

                                    <div className="grid grid-cols-2 gap-3">
                                        <button 
                                            className="py-2 px-4 bg-dropdown-bg text-white rounded-sm font-bold text-sm"
                                            onClick={() => setExpandedId(isExpanded ? null : promo.id)}
                                        >
                                            {t("moreInfo")}
                                        </button>
                                        <button 
                                            className="py-2 px-4 btn-gradient text-black rounded-sm font-bold text-sm"
                                            onClick={() => navigate("/deposit")}
                                        >
                                            {t("apply")}
                                        </button>
                                    </div>

                                    <div 
                                        className={`overflow-hidden transition-all duration-300 ease-in-out ${
                                            isExpanded ? "max-h-[3000px] opacity-100 translate-y-0 scale-100" : "max-h-0 opacity-0 translate-y-4 scale-95 pointer-events-none"
                                        }`}
                                    >
                                        <div className="mt-3 space-y-3 text-white text-xs transition-all duration-300 ease-in-out">

                                            <div className="text-sm text-white">
                                                <p className="font-bold mb-2">
                                                    {t("promotionPeriod")}:
                                                </p>
                                                
                                                <p className="">
                                                    {formatDate(promo.start_date)} - {formatDate(promo.end_date)}
                                                </p>
                                            </div>

                                            {details && (
                                                <div className="text-sm text-white">
                                                    <p className="font-bold mb-2">
                                                        {t("promotionDetails")}:
                                                    </p>
                                                    <style>{`.html-content * { color: white !important; }`}</style>
                                                    <div 
                                                        className="whitespace-pre-line html-content"
                                                        dangerouslySetInnerHTML={{ __html: details }}
                                                    />
                                                </div>
                                            )}

                                            {howToApply && (
                                                <div className="text-sm text-white">
                                                    <p className="font-bold mb-2">
                                                        {t("howToApply")}:
                                                    </p>
                                                    <style>{`.html-content * { color: white !important; }`}</style>
                                                    <div 
                                                        className="whitespace-pre-line html-content"
                                                        dangerouslySetInnerHTML={{ __html: howToApply }}
                                                    />
                                                </div>
                                            )}

                                            {tnc && (
                                                <div className="text-sm text-white">
                                                    <p className="font-bold mb-2">
                                                        {t("termsAndConditions")}:
                                                    </p>
                                                    <style>{`.html-content * { color: white !important; }`}</style>
                                                    <div 
                                                        className="whitespace-pre-line html-content"
                                                        dangerouslySetInnerHTML={{ __html: tnc }}
                                                    />
                                                </div>
                                            )}

                                            <div className="grid grid-cols-2 gap-3 pb-8">
                                                <button 
                                                    className="py-2 px-4 bg-dropdown-bg text-white rounded-sm font-bold text-sm"
                                                    onClick={() => setExpandedId(null)}
                                                >
                                                    {t("viewLess")}
                                                </button>
                                                <button 
                                                    className="py-2 px-4 btn-gradient text-black rounded-sm font-bold text-sm"
                                                    onClick={() => navigate("/deposit")}
                                                >
                                                    {t("apply")}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                        </div>
                    ) : (
                        <div className="flex-center flex-col py-20 text-center">
                            <div className="bg-option-blue p-8 rounded-full mb-4">
                                <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                                </svg>
                            </div>
                            <p className="text-white font-medium">{t("noPromotionAvailable")}</p>
                            <p className="text-gray-400 text-sm mt-1">{t("checkBackLater")}</p>
                        </div>
                    )}
                </div>
            </div>

            <FloatingMenu />
        </section>
    );
};

export default Promotion;
